<script lang="ts">
    import {getInjector} from 'src/core';
    import {KpPopoverPanel} from 'shared/ui-widgets/popover';
    import {Flex} from 'shared/layouts';
    import type {UUID} from 'src/typings';
    import type {Thread} from 'src/features/threads/threads.types';
    import type {ThreadsContext} from 'src/features/threads/services/threads-context.service';
    import {ThreadsContextService} from 'src/features/threads/services/threads-context.service';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {onDestroy, onMount} from 'svelte';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {fly, fade} from 'svelte/transition';
    import {OPEN_THREAD_POPOVER_EVENT} from 'src/features/threads/threads.constants';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import ThreadsSidebar from 'src/features/threads/popover/components/ThreadsSidebar.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import ThreadPopoverHeader from 'src/features/threads/popover/components/ThreadPopoverHeader.svelte';
    import ThreadContent from 'src/features/threads/thread-content/ThreadContent.svelte';
    import NewThreadPopoverPage from 'src/features/threads/popover/components/new-thread/NewThreadPopoverPage.svelte';

    const contextService = getInjector().getByClass(ThreadsContextService);
    const popoverId = 'kp-threads-popover';
    $: buttonId = `${popoverId}-popover-button`;
    $: panelId = `${popoverId}-popover-panel`;

    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-end'));

    let threadContext = contextService.getContextValue();
    const threadContextSubscription = contextService.context$.subscribe((newContext) => threadContext = newContext);

    let popoverOpen = false;

    let page: 'main' | 'new-thread' = 'main';

    let animationDirection = 0;
    let openedThread: Thread | null = null;
    $: selectedThread = getSelectedThread(threadContext, openedThread);

    onMount(() => {
        contextService.eventBus.addEventListener(OPEN_THREAD_POPOVER_EVENT, handleOpenThreadPopover);
    });

    onDestroy(() => {
        cleanup(threadContextSubscription);
        contextService.eventBus.removeEventListener(OPEN_THREAD_POPOVER_EVENT, handleOpenThreadPopover);
    });

    function getSelectedThread(context: ThreadsContext, thread: Thread | null): Thread | null {
        if (!exists(thread)) {
            return null;
        }

        return context.threads.find((t) => t.id === thread.id) ?? null;
    }

    const handleThreadSelectedOrCreated = (event: CustomEvent<Thread>) => {
        animationDirection = -1;
        openedThread = event.detail;
        page = 'main';
    };

    const handleGoToNewThreadPage = () => {
        openedThread = null;
        animationDirection = 1;
        page = 'new-thread';
    };

    const handleGoToMainPage = () => {
        openedThread = null;
        animationDirection = -1;
        page = 'main';
    };

    const handlePopoverClose = () => {
        openedThread = null;
        animationDirection = 0;
        page = 'main';
    };

    const handleOpenThreadPopover = (event: CustomEvent<UUID>) => {
        const foundThread = threadContext.threads.find((t) => t.id === event.detail);
        if (!exists(foundThread)) {
            return;
        }

        popoverOpen = true;
        openedThread = foundThread;
    };

    const handleClosePopoverClick = () => {
        popoverOpen = false;
    };
</script>

<KpButton id={buttonId}
          buttonSize="md"
          buttonStyle="no-background"
          additionalClasses="kp-threads-popover-button"
          use={[floatingRef]}
          popovertarget={panelId}>

    <Flex alignItems="center" gap="s">
        <UIcon icon="messages"/>
        <span class="total-unread-threads" class:unread={threadContext.unreadThreadsCount > 0}>
            {threadContext.unreadThreadsCount}
        </span>
        <div class="icon-container" class:opened={popoverOpen}>
            <UIcon icon="angle-small-down"/>
        </div>
    </Flex>
</KpButton>

<KpPopoverPanel id={panelId}
                use={[floatingContent]}
                additionalClasses="kp-threads-popover-panel"
                popoverMode="manual"
                bind:open={popoverOpen}
                on:popover-close={handlePopoverClose}>

    <div class="kp-threads-popover-content">
        {#if !threadContext.initialized}
            <KpLoadingBlock size="sm" fillAvailableSpace/>
        {/if}

        {#if threadContext.initialized}
            {#if threadContext.loadError}
                <IconedContent icon="exclamation"
                               iconColor="var(--danger-red)"
                               orientation="vertical"
                               align="center"
                               justify="center"
                               fillAvailableSpace>

                    Nastala chyba při načítání!
                </IconedContent>
            {:else}
                {#key page}
                    <div class="anim-container" in:fly={{x: 15 * animationDirection, duration: 250}}>
                        {#if page === 'main'}
                            <div class="sidebar-container">
                                <ThreadsSidebar {selectedThread}
                                                on:close={handleClosePopoverClick}
                                                on:new-thread={handleGoToNewThreadPage}
                                                on:thread-select={handleThreadSelectedOrCreated}/>
                            </div>

                            {#key exists(selectedThread)}
                                <div class="content-container" in:fly={{y: 15, duration: 250}}>
                                    {#if !exists(selectedThread)}
                                        <IconedContent gap="0px"
                                                       icon="messages"
                                                       orientation="vertical"
                                                       align="center"
                                                       justify="center"
                                                       fillAvailableSpace>

                                            Vyberte nebo vytvořte konverzaci
                                        </IconedContent>
                                    {/if}

                                    {#if exists(openedThread)}
                                        {#key openedThread.id}
                                            <div class="thread-content-container" in:fade={{duration: 250}}>
                                                <ThreadPopoverHeader on:close={handleGoToMainPage}
                                                                     thread={selectedThread}/>
                                                <ThreadContent placement="popover" thread={selectedThread}/>
                                            </div>
                                        {/key}
                                    {/if}
                                </div>
                            {/key}
                        {/if}

                        {#if page === 'new-thread'}
                            <NewThreadPopoverPage on:back={handleGoToMainPage}
                                                  on:thread-created={handleThreadSelectedOrCreated}/>
                        {/if}
                    </div>
                {/key}
            {/if}
        {/if}
    </div>
</KpPopoverPanel>

<style lang="less">
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro-erp.less";

    .total-unread-threads {
        font-weight: 500;
        font-size: @font-size-small;

        &.unread {
            color: var(--danger-red);
        }
    }

    .icon-container {
        display: flex;
        transition: rotate 0.2s ease-in-out;
        font-size: @font-size-small;

        &.opened {
            rotate: -180deg;
        }
    }

    .kp-threads-popover-content {
        .flex-grow();
        overflow: hidden;

        .anim-container {
            .flex-grow();
            flex-direction: row;
        }

        .content-container,
        .thread-content-container {
            .flex-grow();
        }

        .sidebar-container {
            width: 270px;
            height: 100%;
            display: flex;
            flex-direction: column;
            border-right: 1px solid @themed-border-default;
        }
    }

    :global {
        .kp-threads-popover-panel {
            --popover-bg-color: @themed-body-bg;
            --popover-border-radius: @border-radius-xl;
            --popover-border-color: @themed-border-default;
            --popover-content-padding: 0;

            width: 800px !important;
            height: 960px !important;
            max-width: 90vw !important;
            max-height: 90vh !important;
            margin-top: @spacing-s;

            .content {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            .kp-button {
                outline: none;
            }
        }
    }
</style>